import map from 'lodash/map';
import keyBy from 'lodash/keyBy';
import { REFUND_ESTIMATION } from '../../constants/endpoints';
import { formatCurrencyValue } from '../../lib/currencyFormatters';
import { orderArrayByConfiguration } from '../../lib/reorderByConfiguration';
import { DEFAULT_REFUND_OPTION_ORDER } from '../../constants/refundOptions';

export const RECEIVE_IS_LOADING_REFUND_ESTIMATION = 'RECEIVE_IS_LOADING_REFUND_ESTIMATION';
export const RECEIVE_REFUND_ESTIMATION = 'RECEIVE_REFUND_ESTIMATION';
export const SELECT_REFUND_OPTION = 'SELECT_REFUND_OPTION';
export const RECEIVE_REFUND_ELIGIBILITY = 'RECEIVE_REFUND_ELIGIBILITY';

const getItemLabel = (item, key) => (item ? item[`${key}_label`] : undefined);

const mergeItemizations = ({
  refundTotals,
  credits,
  debits,
  currencyCode,
  locale,
  refundDisclaimer,
}) => {
  const refundTotalsList = map(refundTotals, (value, key) => ({
    key,
    value,
    prefix: '',
    formatted: formatCurrencyValue({ value, currencyCode, locale }),
    label: getItemLabel(refundTotals, key),
  }));
  const creditsList = map(credits, (value, key) => ({
    key,
    value,
    prefix: '',
    formatted: formatCurrencyValue({ value, currencyCode, locale }),
    label: getItemLabel(credits, key),
  }));
  const debitsList = map(debits, (value, key) => ({
    key,
    value,
    prefix: '-',
    formatted: formatCurrencyValue({ value, currencyCode, locale }),
    label: getItemLabel(debits, key),
  }));
  const refundDisclaimerList = refundDisclaimer
    ? [{ key: 'refund_disclaimer', value: refundDisclaimer }]
    : [];
  const creditsDebitsMap = keyBy(
    [...refundTotalsList, ...creditsList, ...debitsList, ...refundDisclaimerList],
    'key',
  );
  return creditsDebitsMap;
};

export const getRefundEstimation = (order) => async (dispatch, getState) => {
  const {
    page: { productType, browserFingerprint, locale } = {},
    retailerSettings: { name: retailerName } = {},
    returnsSettings: {
      [productType]: {
        refunds: { option_display_order: optionDisplayOrder = DEFAULT_REFUND_OPTION_ORDER } = {},
        refund_option: { options: refundOptions = [] } = {},
      } = {},
    } = {},
    orderInfo: { orderLocale, orderData: { order_info: { id: orderInfoId } = {} } = {} } = {},
  } = getState();

  const requestBody = {
    locale,
    order_info_id: orderInfoId,
    order_number: order.order_number,
    product_type: order.product_type,
    gift: order.gift,
    return_override_code: order.return_override_code,
    return_items: order.return_items.map((item) => ({
      item_info_id: item.item_id,
      return_reason_code_id: item.return_reason_code_id,
      name: item.item_name,
      product: item.product,
      return_qty: item.return_qty,
      exchange_item: item.exchange_item,
    })),
    return_method: {
      type: order.return_method,
      carrier: order.return_carrier,
      service_type: order.shipping_method,
    },
  };

  try {
    dispatch({
      type: RECEIVE_IS_LOADING_REFUND_ESTIMATION,
      data: {
        isLoading: true,
      },
    });
    const response = await fetch(REFUND_ESTIMATION(retailerName), {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'narvar-tfid': browserFingerprint,
      },
      body: JSON.stringify(requestBody),
    });

    const json = await response.json();
    const { estimate, estimate: { methods = [] } = {}, status = '', messages = [] } = json;
    if (status !== 'SUCCESS') throw new Error(messages.join(' '));

    const itemizationsByType = methods.reduce(
      (
        output,
        { type, refund_totals: refundTotals, credits, debits, refund_disclaimer: refundDisclaimer },
      ) => ({
        ...output,
        [type]: mergeItemizations({
          refundTotals,
          credits,
          debits,
          currencyCode: estimate.currency,
          locale: orderLocale || locale,
          refundDisclaimer,
        }),
      }),
      {},
    );

    const refundItems = Object.keys(itemizationsByType);
    const selectedType = refundItems.length === 1 ? refundItems[0] : '';

    const defaultOption =
      refundOptions.length &&
      refundOptions.find((e) => e.default && e.value && itemizationsByType[e.value]);

    const reorderedRefundOptions = orderArrayByConfiguration({
      arrayToOrder: methods,
      configuration: optionDisplayOrder,
      propToCheck: 'type',
    });

    await dispatch({
      type: RECEIVE_REFUND_ESTIMATION,
      data: {
        estimate: { ...estimate, methods: reorderedRefundOptions },
        itemizationsByType,
        selectedType: (defaultOption && defaultOption.value) || selectedType,
      },
    });
  } catch (error) {
    // TODO: Handle if this call fails for some reason
    console.error(error);
  } finally {
    dispatch({
      type: RECEIVE_IS_LOADING_REFUND_ESTIMATION,
      data: {
        isLoading: false,
      },
    });
  }
};

export const selectRefundOption = (selectedType) => ({
  type: SELECT_REFUND_OPTION,
  data: { selectedType },
});

export const receiveRefundEligiblity = (refundEligibility) => ({
  type: RECEIVE_REFUND_ELIGIBILITY,
  data: { refundEligibility },
});
